import { FormulaValidationResult } from '../types/model.types';

/**
 * Parser y validador de fórmulas
 *
 * Valida sintaxis, variables, operadores y detecta problemas potenciales
 * como divisiones por cero o inyección de código.
 */

// Funciones matemáticas permitidas
const MATH_FUNCTIONS = ['sqrt', 'pow', 'abs', 'min', 'max', 'round', 'ceil', 'floor'];

// Funciones / palabras reservadas en MAYÚSCULAS (no son variables aunque parezcan)
const RESERVED_UPPER = new Set([
  'IF',
  'IFS',
  'LOOKUP',
  'SQRT',
  'POW',
  'ABS',
  'MIN',
  'MAX',
  'ROUND',
  'CEIL',
  'FLOOR',
  'TRUE',
  'FALSE',
  'AND',
  'OR',
  'NOT',
  'E',
  'PI',
]);

// Caracteres peligrosos que podrían indicar inyección de código
const DANGEROUS_PATTERNS = [
  /\beval\b/i,
  /\bFunction\b/,
  /\bnew\b/,
  /\bimport\b/,
  /\brequire\b/,
  /\bwindow\b/,
  /\bdocument\b/,
  /\bglobal\b/,
  /\bprocess\b/,
  /\bfetch\b/,
  /\b__proto__\b/,
  /\bconstructor\b/,
  /[;{}[\]\\]/,
  /`/,
  /\$\{/,
];

/**
 * Extrae los nombres de tabla usados como primer argumento de LOOKUP(...).
 * Esos nombres NO son variables — son códigos de LookupTable.
 */
function extractLookupTableNames(formula: string): Set<string> {
  const names = new Set<string>();
  const re = /LOOKUP\s*\(\s*([A-Z_][A-Z0-9_]*)/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(formula.replace(/"[^"]*"|'[^']*'/g, ''))) !== null) {
    names.add(m[1].toUpperCase());
  }
  return names;
}

/**
 * Extrae las variables referenciadas en una fórmula
 */
export function extractVariables(formula: string): string[] {
  // Remover strings entre comillas
  const cleanFormula = formula.replace(/"[^"]*"|'[^']*'/g, '');

  // Detectar nombres de tablas (primer arg de LOOKUP) — no se cuentan como variables
  const tableNames = extractLookupTableNames(formula);

  // Buscar tokens que parecen variables (letras mayúsculas y guiones bajos)
  const matches = cleanFormula.match(/[A-Z_][A-Z0-9_]*/g) || [];

  // Filtrar funciones matemáticas, palabras reservadas y nombres de tabla
  const variables = matches.filter((match) => {
    if (MATH_FUNCTIONS.includes(match.toLowerCase())) return false;
    const upper = match.toUpperCase();
    if (RESERVED_UPPER.has(upper)) return false;
    if (tableNames.has(upper)) return false;
    return true;
  });

  // Remover duplicados
  return Array.from(new Set(variables));
}

/**
 * Verifica que los paréntesis estén balanceados y correctamente anidados
 */
function validateParentheses(formula: string): string | null {
  let depth = 0;
  let position = 0;

  for (const char of formula) {
    position++;
    if (char === '(') {
      depth++;
    } else if (char === ')') {
      depth--;
      if (depth < 0) {
        return `Paréntesis de cierre sin apertura en la posición ${position}`;
      }
    }
  }

  if (depth > 0) {
    const plural = depth > 1 ? 'paréntesis de cierre' : 'paréntesis de cierre';
    return `Faltan ${depth} ${plural}`;
  }

  return null;
}

/**
 * Verifica que no haya paréntesis vacíos: ()
 */
function checkEmptyParentheses(formula: string): string | null {
  if (/\(\s*\)/.test(formula)) {
    return 'La fórmula contiene paréntesis vacíos';
  }
  return null;
}

/**
 * Verifica que los operadores estén correctamente ubicados
 */
function validateOperators(formula: string): string | null {
  // Strip strings entre comillas para no malinterpretar espacios en "FLETE AÉREO" etc.
  const trimmed = formula
    .replace(/"[^"]*"/g, '""')
    .replace(/'[^']*'/g, "''")
    .trim();

  // No puede empezar con un operador multiplicativo/divisor
  if (/^[*/^%]/.test(trimmed)) {
    return 'La fórmula no puede comenzar con un operador multiplicativo';
  }

  // No puede terminar con un operador (aritmético, comparación o lógico)
  if (/[+\-*/^%><=!&|]\s*$/.test(trimmed)) {
    return 'La fórmula no puede terminar con un operador';
  }

  // Detectar operadores consecutivos (excepto negación: ej. A * -B)
  // Permitimos combinaciones como *-, /-, +-, pero no **, //, ++, etc.
  if (/[+\-*/^%]\s*[*/^%]/.test(trimmed)) {
    return 'La fórmula contiene operadores consecutivos inválidos';
  }

  // Detectar doble signo sin operador entre ellos (ej. "A B" sin operador)
  // Esto captura tokens numéricos/variables consecutivos sin operador
  if (
    /[A-Z0-9_]\s+[A-Z_]/.test(trimmed) &&
    !/\b(min|max|pow|sqrt|abs|round|ceil|floor)\s*\(/i.test(trimmed)
  ) {
    // Verificar más cuidadosamente: solo alertar si hay tokens no-función consecutivos
    const tokens = trimmed.match(/[A-Z_][A-Z0-9_]*/g) || [];
    for (let i = 0; i < tokens.length - 1; i++) {
      const current = tokens[i];
      const next = tokens[i + 1];
      // Si ambos no son funciones matemáticas, buscar si hay operador entre ellos
      if (
        !MATH_FUNCTIONS.includes(current.toLowerCase()) &&
        !MATH_FUNCTIONS.includes(next.toLowerCase())
      ) {
        const idx1 = trimmed.indexOf(current);
        const idx2 = trimmed.indexOf(next, idx1 + current.length);
        const between = trimmed.slice(idx1 + current.length, idx2);
        if (!/[+\-*/^%(),><=!?:&|]/.test(between)) {
          return `Falta un operador entre "${current}" y "${next}"`;
        }
      }
    }
  }

  return null;
}

/**
 * Verifica que el operador ternario esté bien formado (cada ? tiene su :)
 */
function validateTernaryOperator(formula: string): string | null {
  const questionMarks = (formula.match(/\?/g) || []).length;
  const colons = (formula.match(/:/g) || []).length;

  if (questionMarks !== colons) {
    return `Operador ternario mal formado: ${questionMarks} "?" y ${colons} ":"`;
  }

  return null;
}

/**
 * Verifica que '=' no se use como asignación (solo permitido en ==, !=, >=, <=)
 */
function validateEqualsSign(formula: string): string | null {
  // Buscar '=' que no sea parte de ==, !=, >=, <=
  // Recorrer la fórmula caracter por caracter
  for (let i = 0; i < formula.length; i++) {
    if (formula[i] === '=') {
      const prev = i > 0 ? formula[i - 1] : '';
      const next = i < formula.length - 1 ? formula[i + 1] : '';

      // Válido si es parte de == (el segundo =)
      if (prev === '=' || prev === '!' || prev === '>' || prev === '<') {
        continue;
      }
      // Válido si es el primer = de ==
      if (next === '=') {
        continue;
      }
      // Si llegamos aquí, es un = solitario (asignación)
      return 'El operador "=" no es válido. Use "==" para comparar igualdad';
    }
  }

  return null;
}

/**
 * Verifica que no haya caracteres no permitidos en la fórmula
 */
function validateAllowedCharacters(formula: string): string | null {
  // Caracteres permitidos: letras, números, operadores, paréntesis, espacios, punto, coma, guión bajo,
  // comparación (><!=), ternario (?:), lógicos (&|), comillas (para strings en LOOKUP)
  // Permite también caracteres acentuados latinos comunes en strings de LOOKUP (ej. "FLETE AÉREO 1").
  const allowedPattern = /^[A-Za-z0-9+\-*/^%().,_ \t\n\r><=!?:&|"'À-ÿ]+$/;

  if (!allowedPattern.test(formula)) {
    // Encontrar el caracter no permitido para un mejor mensaje de error
    for (let i = 0; i < formula.length; i++) {
      if (!allowedPattern.test(formula[i])) {
        return `Caracter no permitido "${formula[i]}" en la posición ${i + 1}`;
      }
    }
    return 'La fórmula contiene caracteres no permitidos';
  }

  return null;
}

/**
 * Detecta patrones peligrosos que podrían indicar inyección de código
 */
function checkDangerousPatterns(formula: string): string | null {
  for (const pattern of DANGEROUS_PATTERNS) {
    if (pattern.test(formula)) {
      return 'La fórmula contiene patrones no permitidos por seguridad';
    }
  }
  return null;
}

/**
 * Detecta posibles divisiones por cero (advertencia, no error)
 */
function checkDivisionByZero(formula: string): string[] {
  const warnings: string[] = [];

  // Detectar división directa por cero: / 0, /0
  if (/\/\s*0(\s|$|\))/.test(formula)) {
    warnings.push('La fórmula contiene una división por cero directa');
  }

  // Detectar que hay divisiones - advertir sobre posibles divisiones por cero dinámicas
  const divisionVarPattern = /\/\s*([A-Z_][A-Z0-9_]*)/g;
  let match;
  while ((match = divisionVarPattern.exec(formula)) !== null) {
    const varName = match[1];
    if (!MATH_FUNCTIONS.includes(varName.toLowerCase())) {
      warnings.push(`División por la variable "${varName}": asegúrese de que su valor no sea cero`);
    }
  }

  return warnings;
}

/**
 * Verifica que las funciones matemáticas se usen correctamente
 */
function validateFunctionCalls(formula: string): string | null {
  // Funciones permitidas en uppercase (LOOKUP/IF/IFS) o lowercase (built-ins)
  const allowedUpper = new Set([
    'LOOKUP',
    'IF',
    'IFS',
    ...MATH_FUNCTIONS.map((f) => f.toUpperCase()),
  ]);

  // Buscar tokens en minúsculas que podrían ser funciones
  const lowerPattern = /\b([a-z]+)\s*\(/g;
  let match;
  while ((match = lowerPattern.exec(formula)) !== null) {
    const funcName = match[1];
    if (!MATH_FUNCTIONS.includes(funcName)) {
      return `Función desconocida: "${funcName}". Funciones permitidas: ${MATH_FUNCTIONS.join(', ')}, LOOKUP, IF, IFS`;
    }
  }

  // Buscar tokens en mayúsculas que aparezcan como llamada a función (TOKEN()).
  // Si no es LOOKUP/IF/IFS, podría ser solo el nombre de una variable usado como string,
  // pero como token() implica llamada, validamos.
  const upperPattern = /\b([A-Z][A-Z0-9_]*)\s*\(/g;
  while ((match = upperPattern.exec(formula)) !== null) {
    const funcName = match[1];
    if (!allowedUpper.has(funcName)) {
      return `Función desconocida: "${funcName}". Funciones permitidas: ${MATH_FUNCTIONS.join(', ')}, LOOKUP, IF, IFS`;
    }
  }

  return null;
}

/**
 * Valida la sintaxis de una fórmula de forma exhaustiva
 */
export function validateFormula(
  formula: string,
  availableVariables: string[]
): FormulaValidationResult {
  // 1. Validar que no esté vacía
  if (!formula || formula.trim() === '') {
    return {
      isValid: false,
      error: 'La fórmula no puede estar vacía',
    };
  }

  const trimmedFormula = formula.trim();

  try {
    // 2. Verificar patrones peligrosos (seguridad primero)
    const dangerousError = checkDangerousPatterns(trimmedFormula);
    if (dangerousError) {
      return {
        isValid: false,
        error: dangerousError,
      };
    }

    // 3. Verificar caracteres permitidos
    const charError = validateAllowedCharacters(trimmedFormula);
    if (charError) {
      return {
        isValid: false,
        error: charError,
      };
    }

    // 4. Verificar paréntesis balanceados
    const parenthesisError = validateParentheses(trimmedFormula);
    if (parenthesisError) {
      return {
        isValid: false,
        error: parenthesisError,
      };
    }

    // 5. Verificar paréntesis vacíos
    const emptyParenError = checkEmptyParentheses(trimmedFormula);
    if (emptyParenError) {
      return {
        isValid: false,
        error: emptyParenError,
      };
    }

    // 6. Verificar funciones matemáticas
    const functionError = validateFunctionCalls(trimmedFormula);
    if (functionError) {
      return {
        isValid: false,
        error: functionError,
      };
    }

    // 7. Verificar operadores
    const operatorError = validateOperators(trimmedFormula);
    if (operatorError) {
      return {
        isValid: false,
        error: operatorError,
      };
    }

    // 7b. Verificar operador ternario
    const ternaryError = validateTernaryOperator(trimmedFormula);
    if (ternaryError) {
      return {
        isValid: false,
        error: ternaryError,
      };
    }

    // 7c. Verificar uso de = (solo permitido como parte de ==, !=, >=, <=)
    const equalsError = validateEqualsSign(trimmedFormula);
    if (equalsError) {
      return {
        isValid: false,
        error: equalsError,
      };
    }

    // 8. Extraer variables usadas y verificar que existan
    const usedVariables = extractVariables(trimmedFormula);
    const dependencies = usedVariables;

    const unknownVariables = usedVariables.filter((v) => !availableVariables.includes(v));
    if (unknownVariables.length > 0) {
      const plural = unknownVariables.length > 1 ? 's' : '';
      return {
        isValid: false,
        error: `Variable${plural} no definida${plural}: ${unknownVariables.join(', ')}`,
      };
    }

    // 9. Verificar que la fórmula no sea solo un número
    // (es válido, pero advertimos por si es un error del usuario)
    const warnings: string[] = [];
    if (/^\d+(\.\d+)?$/.test(trimmedFormula)) {
      warnings.push(
        'La fórmula es un valor constante. Si necesita una variable calculada, use variables en la fórmula.'
      );
    }

    // 10. Detectar posibles divisiones por cero
    const divisionWarnings = checkDivisionByZero(trimmedFormula);
    warnings.push(...divisionWarnings);

    // 11. Verificar auto-referencia (una variable que se referencia a sí misma)
    // No lo hacemos aquí porque no tenemos el código de la variable actual,
    // pero el caller (variable-form) ya filtra availableVariables

    return {
      isValid: true,
      dependencies,
      warnings: warnings.length > 0 ? warnings : undefined,
    };
  } catch (error: any) {
    return {
      isValid: false,
      error: error.message || 'Error inesperado al validar la fórmula',
    };
  }
}

/**
 * Detecta dependencias circulares en las variables
 */
export function detectCircularDependencies(
  variables: Array<{ code: string; formula?: string }>
): string[] {
  const dependencies = new Map<string, string[]>();

  // Construir grafo de dependencias
  variables.forEach((variable) => {
    if (variable.formula) {
      const deps = extractVariables(variable.formula);
      dependencies.set(variable.code, deps);
    } else {
      dependencies.set(variable.code, []);
    }
  });

  // Detectar ciclos usando DFS
  const visited = new Set<string>();
  const recursionStack = new Set<string>();
  const circularVariables: string[] = [];

  function hasCycle(variable: string): boolean {
    visited.add(variable);
    recursionStack.add(variable);

    const deps = dependencies.get(variable) || [];
    for (const dep of deps) {
      if (!visited.has(dep)) {
        if (hasCycle(dep)) {
          circularVariables.push(variable);
          return true;
        }
      } else if (recursionStack.has(dep)) {
        circularVariables.push(variable);
        return true;
      }
    }

    recursionStack.delete(variable);
    return false;
  }

  variables.forEach((variable) => {
    if (!visited.has(variable.code)) {
      hasCycle(variable.code);
    }
  });

  return Array.from(new Set(circularVariables));
}

/**
 * Ordena las variables en orden topológico para evaluación
 */
export function topologicalSort(variables: Array<{ code: string; formula?: string }>): string[] {
  const dependencies = new Map<string, string[]>();
  const inDegree = new Map<string, number>();

  // Construir grafo de dependencias
  variables.forEach((variable) => {
    inDegree.set(variable.code, 0);
    if (variable.formula) {
      const deps = extractVariables(variable.formula);
      dependencies.set(variable.code, deps);
    } else {
      dependencies.set(variable.code, []);
    }
  });

  // Construir grafo inverso: dependents[A] = nodos que dependen de A
  const dependents = new Map<string, string[]>();
  variables.forEach((v) => dependents.set(v.code, []));

  dependencies.forEach((deps, variable) => {
    deps.forEach((dep) => {
      if (dependents.has(dep)) {
        dependents.get(dep)!.push(variable);
      }
    });
    // in-degree = cantidad de dependencias que ESTE nodo tiene (solo las conocidas)
    inDegree.set(variable, deps.filter((d) => inDegree.has(d)).length);
  });

  // Kahn's algorithm
  const queue: string[] = [];
  const result: string[] = [];

  // Agregar nodos sin dependencias (in-degree 0)
  inDegree.forEach((degree, variable) => {
    if (degree === 0) {
      queue.push(variable);
    }
  });

  while (queue.length > 0) {
    const variable = queue.shift()!;
    result.push(variable);

    // Decrementar in-degree de los nodos que dependen de este
    const deps = dependents.get(variable) || [];
    deps.forEach((dep) => {
      const newDegree = (inDegree.get(dep) || 0) - 1;
      inDegree.set(dep, newDegree);
      if (newDegree === 0) {
        queue.push(dep);
      }
    });
  }

  return result;
}
